// client/src/dnd/emitter_tool/components/panels/SubEmittersPanel.js
import React, { useMemo, useRef, useState, useEffect } from "react";

const OPEN_KEY = "EmitterTool:SubEmittersPanelOpenV1";

function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {}
  }, [open]);
  return (
    <div
      style={{
        border: "1px solid #334155",
        borderRadius: 8,
        marginBottom: 12,
        background: "#0b0f17",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          fontWeight: 600,
        }}
      >
        <span>{title}</span>
        <span style={{ opacity: 0.75 }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ padding: 10 }}>{children}</div>}
    </div>
  );
}

const deepClone = (v) => JSON.parse(JSON.stringify(v ?? null));
const uid = () => Math.random().toString(36).slice(2, 10);

const rowBase = {
  display: "grid",
  gridTemplateColumns: "28px minmax(0,1fr) auto auto auto",
  alignItems: "center",
  gap: 6,
  padding: "6px 6px",
  borderRadius: 8,
  border: "1px solid #2a3547",
  background: "#0c121c",
};
const rowParent = {
  ...rowBase,
  border: "1px solid #35517a",
  background: "linear-gradient(180deg,#0f1726,#0b1220)",
  gridTemplateColumns: "28px minmax(0,1fr) auto",
};
const selectBtn = (active) => ({
  textAlign: "left",
  width: "100%",
  padding: "6px 8px",
  borderRadius: 6,
  border: `1px solid ${active ? "#6aa3ff" : "#2a3547"}`,
  background: active ? "#142033" : "#0b0f17",
  color: "white",
  fontSize: 12,
  cursor: "pointer",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});
const handleCell = {
  userSelect: "none",
  cursor: "grab",
  textAlign: "center",
  opacity: 0.8,
};
const iconBtn = {
  background: "transparent",
  border: "1px solid #3b475c",
  color: "white",
  borderRadius: 6,
  padding: "2px 6px",
  fontSize: 12,
  lineHeight: 1.2,
  cursor: "pointer",
};
const dangerBtn = { ...iconBtn, color: "#ef4444", borderColor: "#ef4444" };
const primaryBtn = {
  background: "linear-gradient(180deg, #2a3a54, #1f2a3b)",
  border: "1px solid #3b475c",
  color: "white",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 12,
  cursor: "pointer",
};

const smallRow = {
  display: "grid",
  gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
  gap: 6,
  marginTop: 6,
  background: "#0b0f17",
  border: "1px solid #1e2533",
  borderRadius: 6,
  padding: "6px",
  fontSize: 11,
  color: "white",
};
const colFull = {
  gridColumn: "1 / span 2",
  display: "grid",
  gridTemplateColumns: "minmax(0,1fr) auto",
  gap: 6,
  alignItems: "center",
};
const labelStyle = { fontSize: 11, color: "#9ca3af", lineHeight: 1.2 };
const selectStyle = {
  width: "100%",
  background: "#0b0d12",
  color: "white",
  border: "1px solid #374151",
  borderRadius: 6,
  padding: "4px 6px",
  fontSize: 11,
};
const inputStyle = {
  width: "100%",
  background: "#0b0d12",
  color: "white",
  border: "1px solid #374151",
  borderRadius: 6,
  padding: "4px 6px",
  fontSize: 11,
};

function useSettingsDirtyEmitter() {
  const rafRef = useRef(0);
  const pendingRef = useRef(null);
  return (detail) => {
    pendingRef.current = {
      panel: "SubEmittersPanel",
      ts: performance.now(),
      ...(detail || {}),
    };
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:SettingsDirty", {
            detail: pendingRef.current,
          })
        );
      } catch {}
      rafRef.current = 0;
      pendingRef.current = null;
    });
  };
}

function normalizeStartModeUI(mode) {
  const m = String(mode || "").toLowerCase();
  if (m === "afterparent" || m === "after_parent" || m === "after-parent") return "afterParent";
  if (m === "immediate") return "delay"; // map legacy “immediate” to “delay” with 0ms
  if (m === "delay" || m === "wait" || m === "waitms" || m === "wait_ms") return "delay";
  return "delay";
}

function SubEmittersPanel({
  effectSettings = {},
  setEffectSettings,
  title = "Emitters",
  activeEmitterRef,
  onSelectEmitter,
  parentSnapshot,
}) {
  const emitDirty = useSettingsDirtyEmitter();

  const list = Array.isArray(effectSettings?.subEmitters)
    ? effectSettings.subEmitters
    : [];
  const parentIndexRaw = Number.isFinite(effectSettings?.subEmittersParentIndex)
    ? effectSettings.subEmittersParentIndex
    : 0;
  const parentIndex = Math.min(
    Math.max(0, Math.floor(parentIndexRaw)),
    list.length
  );

  const selectedId =
    activeEmitterRef?.type === "child" ? activeEmitterRef.id : null;

  useEffect(() => {
    const handler = (ev) => {
      const isChild = !!selectedId;
      if (isChild) {
        ev.stopImmediatePropagation?.();
        try {
          window.dispatchEvent(
            new CustomEvent(`EmitterTool:GlowConfig:child:${selectedId}`, {
              detail: ev.detail,
            })
          );
        } catch {}
      }
    };
    try {
      window.addEventListener("EmitterTool:GlowConfig", handler, { capture: true });
    } catch {}
    return () => {
      try {
        window.removeEventListener("EmitterTool:GlowConfig", handler, { capture: true });
      } catch {}
    };
  }, [selectedId]);

  const broadcastChildRulesAll = (arr, reason) => {
    const now = performance.now();
    for (let i = 0; i < arr.length; i++) {
      const child = arr[i];
      if (!child || !child.id) continue;
      const startModeNorm = normalizeStartModeUI(child.startMode);
      const delayMsVal =
        startModeNorm === "delay"
          ? Math.max(0, Number(child.delayMs) || 0)
          : 0;
      const payload = {
        ts: now,
        reason,
        id: child.id,
        orderIndex: i,
        name: child.name || "",
        enabled: child.enabled !== false,
        startMode: startModeNorm,     // only "delay" or "afterParent"
        delayMs: delayMsVal,          // 0ms == immediate
        runForMode: child.runForMode || "inherit",
        runDurationS: Number(child.runDurationS) || 0.5,
      };
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:SubEmitterRule", {
            detail: payload,
          })
        );
      } catch {}
    }
  };

  const safeSet = (patch, reason = "effectSettings") => {
    if (typeof setEffectSettings !== "function") return;
    setEffectSettings((cur) => {
      const next = {
        ...(cur || {}),
        ...(typeof patch === "function" ? patch(cur || {}) : patch),
      };
      return next;
    });
    emitDirty({ changed: reason });
  };

  const syncList = (
    next,
    nextParentIndex = parentIndex,
    reason = "subEmitters.update"
  ) => {
    // Normalize legacy “immediate” into delay:0 on save
    const normalized = next.map((ch) => {
      const sm = normalizeStartModeUI(ch.startMode);
      return {
        ...ch,
        startMode: sm,
        delayMs: sm === "delay" ? Math.max(0, Number(ch.delayMs) || 0) : 0,
      };
    });

    safeSet(
      {
        subEmitters: normalized,
        subEmittersEnabled: normalized.length > 0,
        subEmittersParentIndex: Math.min(
          Math.max(0, nextParentIndex),
          normalized.length
        ),
      },
      reason
    );
    broadcastChildRulesAll(normalized, reason);
  };

  const addChild = () => {
    const id = uid();
    const newChild = {
      id,
      name: `Child ${list.length + 1}`,
      enabled: true,
      // New default: Delay with 0ms (acts like “immediate”)
      startMode: "delay",
      delayMs: 0,
      runForMode: "inherit",
      runDurationS: 0.5,
      overrides: {
        emitterShape: deepClone(parentSnapshot?.emitterShape),
        shapeSettings: deepClone(parentSnapshot?.shapeSettings),
        effectSettings: deepClone(parentSnapshot?.effectSettings),
        selectedSprite: deepClone(parentSnapshot?.selectedSprite),
      },
    };
    const next = [...list, newChild];
    const nextParentIndex =
      parentIndex === list.length ? next.length : parentIndex;
    syncList(next, nextParentIndex, "subEmitters.add");
    onSelectEmitter?.({ type: "child", id });
    try {
      window.dispatchEvent(
        new CustomEvent("EmitterTool:ScopeChanged", {
          detail: {
            ts: performance.now(),
            scopeType: "child",
            scopeId: id,
          },
        })
      );
    } catch {}
  };

  const removeChild = (id) => {
    const idx = list.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const next = list.filter((s) => s.id !== id);
    const nextParentIndex =
      idx < parentIndex ? Math.max(0, parentIndex - 1) : parentIndex;
    syncList(next, nextParentIndex, "subEmitters.remove");
    if (selectedId === id) {
      onSelectEmitter?.({ type: "parent" });
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:ScopeChanged", {
            detail: {
              ts: performance.now(),
              scopeType: "parent",
              scopeId: "parent",
            },
          })
        );
      } catch {}
    }
  };

  const renameChild = (id, name) => {
    const next = list.map((s) => (s.id === id ? { ...s, name } : s));
    syncList(next, parentIndex, "subEmitters.rename");
  };

  const toggleChild = (id) => {
    const next = list.map((s) =>
      s.id === id ? { ...s, enabled: !s.enabled } : s
    );
    syncList(next, parentIndex, "subEmitters.toggle");
  };

  const updateChildField = (id, field, value) => {
    const next = list.map((s) =>
      s.id === id ? { ...s, [field]: value } : s
    );
    syncList(next, parentIndex, "subEmitters.fieldUpdate");
  };

  const updateChildFields = (id, patchObj) => {
    const next = list.map((s) =>
      s.id === id ? { ...s, ...patchObj } : s
    );
    syncList(next, parentIndex, "subEmitters.fieldsUpdate");
  };

  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const startEdit = (child) => {
    setEditingId(child.id);
    setEditingText(child.name || "");
  };

  const rows = useMemo(() => {
    const r = list.slice();
    r.splice(parentIndex, 0, { __parent: true });
    return r;
  }, [list, parentIndex]);

  const rowIsParent = (ri) => !!rows[ri]?.__parent;
  const rowToChildIndex = (ri) => (ri < parentIndex ? ri : ri - 1);

  const dragFrom = useRef(null);
  const onDragStart = (ri) => (e) => {
    dragFrom.current = ri;
    try {
      e.dataTransfer.setData("text/plain", String(ri));
    } catch {}
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOverCap = (ri) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDropCap = (toRI) => (e) => {
    e.preventDefault();
    const fromRI =
      dragFrom.current != null
        ? dragFrom.current
        : Number(e.dataTransfer.getData("text/plain"));
    dragFrom.current = null;
    if (!Number.isInteger(fromRI) || fromRI === toRI) return;

    if (rowIsParent(fromRI)) {
      const newParentIndex = Math.min(Math.max(0, toRI), list.length);
      syncList(
        list.slice(),
        newParentIndex,
        "subEmitters.parentIndex"
      );
      return;
    }

    const fromCI = rowToChildIndex(fromRI);
    const copy = list.slice();
    const [moved] = copy.splice(fromCI, 1);

    let nextParentIndex = parentIndex;
    if (fromRI < parentIndex) nextParentIndex = Math.max(0, parentIndex - 1);

    if (rowIsParent(toRI)) {
      copy.splice(nextParentIndex, 0, moved);
      nextParentIndex += 1;
    } else {
      let toCI = rowToChildIndex(toRI);
      if (fromRI < toRI) toCI -= 1;
      const clamped = Math.min(Math.max(0, toCI), copy.length);
      copy.splice(clamped, 0, moved);
    }

    syncList(copy, nextParentIndex, "subEmitters.reorder");
  };

  return (
    <CollapsibleBox title={title} defaultOpen>
      <div style={{ display: "grid", gap: 6 }}>
        {rows.map((row, ri) => {
          const isParent = !!row.__parent;
          const selected = isParent
            ? activeEmitterRef?.type !== "child"
            : activeEmitterRef?.type === "child" &&
              activeEmitterRef?.id === row.id;
          const isEditing = !isParent && editingId === row.id;

          // Map legacy “immediate” to UI “delay” with 0ms
          const startModeUI = normalizeStartModeUI(row.startMode);
          const delayMs = Math.max(
            0,
            Number(
              startModeUI === "delay"
                ? row.delayMs
                : 0
            ) || 0
          );
          const runForMode = row.runForMode || "inherit";
          const runDurationS = Number(row.runDurationS) || 0.5;

          return (
            <div
              key={isParent ? "__parent__" : row.id}
              style={{ display: "grid", gap: 4 }}
            >
              <div
                draggable={!isEditing}
                onDragStart={onDragStart(ri)}
                onDragOverCapture={onDragOverCap(ri)}
                onDropCapture={onDropCap(ri)}
                role="listitem"
                style={isParent ? rowParent : rowBase}
                title={
                  isParent
                    ? "Parent (drag to reorder in stack)"
                    : "Drag to reorder"
                }
              >
                <div style={handleCell} aria-hidden>
                  ⇅
                </div>

                {isParent ? (
                  <button
                    type="button"
                    style={selectBtn(selected)}
                    onClick={() => {
                      onSelectEmitter?.({ type: "parent" });
                      try {
                        window.dispatchEvent(
                          new CustomEvent("EmitterTool:ScopeChanged", {
                            detail: {
                              ts: performance.now(),
                              scopeType: "parent",
                              scopeId: "parent",
                            },
                          })
                        );
                      } catch {}
                    }}
                    aria-current={selected ? "true" : "false"}
                  >
                    Parent
                  </button>
                ) : isEditing ? (
                  <input
                    autoFocus
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    onBlur={() => {
                      const next = editingText.trim();
                      if (next) renameChild(editingId, next);
                      setEditingId(null);
                      setEditingText("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const next = editingText.trim();
                        if (next) renameChild(editingId, next);
                        setEditingId(null);
                        setEditingText("");
                      }
                      if (e.key === "Escape") {
                        setEditingId(null);
                        setEditingText("");
                      }
                    }}
                    style={{
                      background: "#0b0d12",
                      color: "white",
                      border: "1px solid #374151",
                      borderRadius: 6,
                      padding: "6px 8px",
                      fontSize: 12,
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    style={selectBtn(selected)}
                    onClick={() => {
                      onSelectEmitter?.({ type: "child", id: row.id });
                      try {
                        window.dispatchEvent(
                          new CustomEvent("EmitterTool:ScopeChanged", {
                            detail: {
                              ts: performance.now(),
                              scopeType: "child",
                              scopeId: row.id,
                            },
                          })
                        );
                      } catch {}
                    }}
                    title={
                      row.enabled
                        ? "Edit this sub-emitter"
                        : "Disabled (toggle to enable)"
                    }
                    aria-current={selected ? "true" : "false"}
                  >
                    {(row.name || "").trim() || "Child"}
                    {row.enabled ? "" : " (off)"}
                  </button>
                )}

                {!isParent && (
                  <>
                    <button
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => toggleChild(row.id)}
                      title={row.enabled ? "Disable" : "Enable"}
                      aria-pressed={row.enabled ? "true" : "false"}
                      style={iconBtn}
                    >
                      {row.enabled ? "☑" : "☐"}
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => startEdit(row)}
                      title="Rename"
                      aria-label={`Rename ${row.name ?? "child"}`}
                      style={iconBtn}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => removeChild(row.id)}
                      title="Delete"
                      aria-label={`Delete ${row.name ?? "child"}`}
                      style={dangerBtn}
                    >
                      ✕
                    </button>
                  </>
                )}
              </div>

              {!isParent && (
                <div style={smallRow}>
                  <div style={colFull}>
                    <div style={labelStyle}>Start</div>
                    <select
                      style={selectStyle}
                      value={startModeUI}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "afterParent") {
                          updateChildFields(row.id, {
                            startMode: "afterParent",
                            delayMs: 0,
                          });
                        } else {
                          // v === "delay"
                          // Keep existing delay if present, else default to 0 (immediate)
                          const curDelay = Number(row.delayMs);
                          const nextDelay = Number.isFinite(curDelay) ? Math.max(0, curDelay) : 0;
                          updateChildFields(row.id, {
                            startMode: "delay",
                            delayMs: nextDelay,
                          });
                        }
                      }}
                    >
                      <option value="delay">Delay</option>
                      <option value="afterParent">After parent ends</option>
                    </select>
                  </div>

                  {startModeUI === "delay" && (
                    <div style={colFull}>
                      <div style={labelStyle}>Delay (ms) — 0 = immediate</div>
                      <input
                        style={inputStyle}
                        type="number"
                        min={0}
                        value={delayMs}
                        onChange={(e) => {
                          const v = Math.max(0, Number(e.target.value) || 0);
                          updateChildFields(row.id, {
                            startMode: "delay",
                            delayMs: v,
                          });
                        }}
                      />
                    </div>
                  )}

                  <div style={colFull}>
                    <div style={labelStyle}>Stop</div>
                    <select
                      style={selectStyle}
                      value={runForMode}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "timed") {
                          updateChildFields(row.id, {
                            runForMode: "timed",
                          });
                        } else {
                          updateChildFields(row.id, {
                            runForMode: "inherit",
                          });
                        }
                      }}
                    >
                      <option value="inherit">With parent</option>
                      <option value="timed">Timed…</option>
                    </select>
                  </div>

                  {runForMode === "timed" && (
                    <div style={colFull}>
                      <div style={labelStyle}>Duration (s)</div>
                      <input
                        style={inputStyle}
                        type="number"
                        min={0}
                        step={0.1}
                        value={runDurationS}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          updateChildField(
                            row.id,
                            "runDurationS",
                            Number.isFinite(v) ? v : 0
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          onClick={addChild}
          style={primaryBtn}
          title="Create a new sub-emitter from the parent"
        >
          + New sub-emitter
        </button>
      </div>

      <div
        style={{
          fontSize: 11,
          opacity: 0.75,
          marginTop: 8,
        }}
      >
        Tip: Drag rows to set visual order. The “Parent” row can sit anywhere
        in the stack. Select a row to make all panels below edit that exact
        emitter. Use Delay = 0ms for an immediate start.
      </div>
    </CollapsibleBox>
  );
}

export default SubEmittersPanel;
export { SubEmittersPanel };
